<template>
    <div class="detail-view" ref="detailView">
        <!-- Sticky Headband Navigation -->
        <div class="headband-sticky" ref="headband" :style="{ top: stickyTopOffset + 'px' }">
            <div class="headband-container bg-white py-3 d-flex align-items-center">
                <section
                    :id="'splide-communities-' + randomId"
                    class="splide splide-communities flex-grow-1"
                    :aria-label="$t('CommunityDetail.navAriaLabel')"
                >
                    <div class="splide__track">
                        <div class="splide__list d-flex align-items-center">
                            <div
                                v-for="(community, index) in communities"
                                :key="index"
                                class="splide__slide"
                            >
                                <div
                                    class="community-bubble cursorPointer"
                                    :class="{ 'active': selectedCommunity._id === community._id }"
                                    :style="selectedCommunity._id === community._id ? { backgroundColor: community.primaryColourHexCode || '#003D72' } : {}"
                                    @click="switchCommunity(community)"
                                    tabindex="0"
                                    @keydown.enter="switchCommunity(community)"
                                >
                                    <div class="d-flex justify-content-center text-center">
                                        <img v-if="community.logo && community.logo.svg" 
                                             :src="community.logo.svg" 
                                             :alt="community.name"
                                             class="bubble-logo py-1">
                                        <span v-if="!community.logo || !community.logo.svg" class="align-content-around align-self-center bubble-name text-nowrap">{{ community.name }}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- All Communities Button -->
                            <div class="splide__slide">
                                <div
                                    class="community-bubble cursorPointer"
                                    @click="closeDetail"
                                    tabindex="0"
                                    @keydown.enter="closeDetail"
                                >
                                    <div class="d-flex justify-content-center text-center">
                                        <span class="align-content-around align-self-center bubble-name text-nowrap">{{ $t('CommunityDetail.allCommunities') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                
                <!-- Close Button -->
                <button class="btn btn-link p-0 close-btn" 
                        @click="closeDetail"
                        :aria-label="$t('CommunityDetail.close')">
                    <svg width="24" height="24" fill="none" stroke="#000" stroke-width="2" viewBox="0 0 24 24">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Community Detail Content -->
        <div class="detail-content container-fluid pb-5">
            <div class="row">
                <div class="col-12">
                    <div class="mb-5 rounded-corners-20 position-relative overflow-hidden community-header-main" 
                        :style="{ background: 'linear-gradient(' + selectedCommunity.primaryColourHexCode + ' , ' + selectedCommunity.secondaryColourHexCode + ')' }">
                        <!-- Background image overlay -->
                        <div v-if="selectedCommunity.background && selectedCommunity.background.svg" 
                             class="community-header-bg-image"
                             :style="{ backgroundImage: `url(${selectedCommunity.background.svg})` }">
                        </div>
                        
                        <div class="row align-items-center px-5 py-3 position-relative">
                            <div class="col-md-8">
                                <img v-if="selectedCommunity.logo && selectedCommunity.logo.svg" 
                                     :src="selectedCommunity.logo.svg" 
                                     :alt="selectedCommunity.name"
                                     class="community-detail-logo">
                                <h1 class="col-xxl-11 font-weight-bold px-0 text-break text-white">{{ selectedCommunity.slogan }}</h1>
                            </div>
                            <div class="col-md-4 text-center" v-if="brandData && brandData.community_header_image">
                                <img :src="brandData.community_header_image" 
                                     :alt="selectedCommunity.name + ' header image'"
                                     class="community-header-image img-fluid">
                            </div>
                        </div>

                        <!-- Bottom band (headline + CTA) -->
                        <div v-if="brandData && brandData.newsletter_subscription"
                             class="community-header-band d-flex flex-column flex-md-row align-items-center px-4 py-3 justify-content-between"
                             :style="{ backgroundColor: (brandData && brandData.community_band_bg_color) ? brandData.community_band_bg_color : '#f2f2f2' }">
                            <div class="text-center text-md-left">
                                <div class="band-headline font-weight-bold">
                                    {{ $t('CommunityDetail.newsletterHeadline') }}
                                </div>
                                <div class="band-subline">
                                    {{ $t('CommunityDetail.newsletterSubline') }}
                                </div>
                            </div>
                            <button
                                class="btn btn-lg mt-3 mt-md-0 px-4 rounded-corners-20 ml-4 newsletter-btn"
                                :style="{ backgroundColor: selectedCommunity.primaryColourHexCode || '#dc3545', borderColor: selectedCommunity.primaryColourHexCode || '#dc3545' }"
                                data-toggle="modal"
                                data-target="#newsletterModal">
                                {{ $t('CommunityDetail.subscribe') }}
                            </button>
                        </div>
                    </div>

                    <!-- Intro Content Sections -->
                    <div class="row mb-5">
                        <div class="col-md-6">
                            <div class="card h-100 rounded-corners-20"  v-if="brandData && brandData.community_about">
                                <div class="card-body">
                                    <p class="card-text" v-if="brandData && brandData.community_about" v-html="brandData.community_about"></p>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="card h-100 rounded-corners-20" v-if="brandData && brandData.community_audience">
                                <div class="card-body">
                                    <p class="card-text" v-if="brandData && brandData.community_audience" v-html="brandData.community_audience"></p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Category Sliders -->
                    <div class="row mb-5" v-if="selectedCommunity.categories && selectedCommunity.categories.length">
                        <div class="col-12">
                            <categoryFeature 
                                :key="'category-' + selectedCommunity.categories[0]._id + '-' + selectedCommunity._id"
                                :aCategoryId="selectedCommunity.categories[0]._id"
                                :brandData="selectedCommunity"
                                :categories="selectedCommunity.categories"
                                :settingsPerm="communitySettingsPerm"
                                @pocs-fetched="onCategoryPocsFetched"
                                :showBadgeTooltip="true"
                                :perPageBreakpoints="{
                                    default: 3,
                                    1600: 3,
                                    1280: 2,
                                    991: 2,
                                    767: 2,
                                    575: 1
                                }"
                            />
                        </div>
                    </div>
                
                    <!-- RHEINGOLD (Conferences) Slider -->
                    <div class="row mb-5">
                        <div class="col-12">
                            <brand-poc-feature 
                                v-if="communitySettingsPerm && selectedCommunity"
                                :key="'rheingold-' + selectedCommunity._id"
                                :settingsPerm="communitySettingsPerm"
                                queryType="RHEINGOLD"
                                :pageSize="5"
                                :showBadgeTooltip="true"
                                :perPageBreakpoints="{
                                    default: 3,
                                    1600: 3,
                                    1280: 3,
                                    991: 2,
                                    767: 2,
                                    575: 1
                                }"
                            />
                        </div>
                    </div>

                    <!-- Magazines Slider -->
                    <div class="row mb-5">
                        <div class="col-12">
                            <magazines-feature 
                                v-if="settingsPerm && selectedCommunity"
                                :key="'magazines-' + selectedCommunity._id"
                                :settingsPerm="settingsPerm"
                                :brandData="selectedCommunity"
                                :perPageBreakpoints="{
                                    default: 5,
                                    1600: 5,
                                    1280: 4,
                                    991: 3,
                                    767: 2,
                                    575: 2
                                }"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Newsletter Modal -->
        <div class="modal fade" id="newsletterModal" tabindex="-1" role="dialog" aria-labelledby="newsletterModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content rounded-corners-20 position-relative">
                    <button type="button" class="close position-absolute" style="top: 10px; right: 10px; z-index: 10;" data-dismiss="modal" :aria-label="$t('CommunityDetail.close')">
                            <span aria-hidden="true">&times;</span>
                    </button>
                    <div class="modal-body p-0" v-html="brandData.newsletter_subscription"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Splide from '@splidejs/splide';
import magazinesFeature from '../brand/magazinesFeature.vue';
import brandPocFeature from '../brand/brandPocFeature.vue';
import CategoryFeature from '../brand/CategoryFeature.vue';

export default {
    name: 'communityDetail',
    components: {
        magazinesFeature,
        brandPocFeature,
        CategoryFeature
    },
    props: {
        selectedCommunity: {
            type: Object,
            required: true
        },
        communities: {
            type: Array,
            required: true
        },
        brandData: {
            type: Object,
            default: null
        },
        settingsPerm: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            randomId: Math.random().toString(36).substring(2, 15),
            splide: null,
            stickyTopOffset: 0,
            categoryPocData: {},
            resizeTimeout: null
        };
    },
    computed: {
        communitySettingsPerm() {
            // Merge settingsPerm with the current community's slug
            if (!this.settingsPerm) return null;
            return {
                ...this.settingsPerm,
                brandSlug: this.selectedCommunity?.slug || this.settingsPerm.brandSlug
            };
        }
    },
    methods: {
        switchCommunity(community) {
            this.categoryPocData = {};
            this.$emit('switch-community', community);
        },
        closeDetail() {
            this.categoryPocData = {};
            this.$emit('close');
        },
        onCategoryPocsFetched(data) {
            // Store the fetched POCs from child component            
            this.categoryPocData[data.categoryId] = data.pocs;
        },
        handleEscKey(event) {
            if (event.key === 'Escape') {
                this.closeDetail();
            }
        },
        calculateStickyOffset() {
            // Calculate offset from WordPress admin bar and website's main navigation
            let topOffset = 0;
            
            // // Check for WordPress admin bar
            // const adminBar = document.getElementById('wpadminbar');
            // if (adminBar) {
            //     topOffset += adminBar.offsetHeight;
            // }
            
            // Check for website's main navigation menu (navbar, header, etc)
            const navbar = document.querySelector('nav, .navbar, header, .site-header');
            if (navbar) {
                topOffset += navbar.offsetHeight;
            }
            
            this.stickyTopOffset = topOffset;
        },
        handleResize() {
            // Debounced resize handler to recalculate offset when nav height changes
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
                this.calculateStickyOffset();
            }, 150); // 150ms debounce
        },
        mountSplide() {
            this.$nextTick(() => {
                if (!this.splide && this.communities && this.communities.length > 0) {
                    this.splide = new Splide(`#splide-communities-${this.randomId}`, {
                        type: 'slide',
                        perPage: 5,
                        perMove: 1,
                        gap: '0.5rem',
                        rewind: false,
                        pagination: false,
                        arrows: true,
                        drag: 'free',
                        focus: 0,
                        trimSpace: true,
                        padding: {
                            left: '1rem',
                            right: '1rem',
                        },
                        breakpoints: {
                            1600: { perPage: 5 },
                            1280: { perPage: 4 },
                            991: { perPage: 3 },
                            767: { perPage: 2 },
                            575: { 
                                perPage: 2,
                                arrows: true
                            },
                            480: {
                                perPage: 1,
                                arrows: true
                            }
                        }
                    });
                    this.splide.mount();
                }
            });
        }
    },
    mounted() {
        window.addEventListener('keydown', this.handleEscKey);
        window.addEventListener('resize', this.handleResize);
        this.calculateStickyOffset();
        this.mountSplide();
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleEscKey);
        window.removeEventListener('resize', this.handleResize);
        
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        if (this.splide) {
            this.splide.destroy();
        }
    }
};
</script>

<style scoped>
.cursorPointer {
    cursor: pointer;
}

/* Headband Styles */
.headband-sticky {
    position: sticky;
    z-index: 1000;
}

.headband-container {
    max-width: 1920px;
    margin: 0 auto;
    padding-left: 0;
    padding-right: 0;
}

.splide-communities {
    padding: 5px 0;
    width: 100%;
}

.splide-communities .splide__slide {
    width: auto;
}

.splide-communities .splide__arrow {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 36px;
    height: 36px;
    opacity: 1;
}

.splide-communities .splide__arrow:hover {
    background: rgba(255, 255, 255, 0.35);
}

.splide-communities .splide__arrow svg {
    fill: white;
}

.splide-communities .splide__arrow--prev {
    left: 1rem;
}

.splide-communities .splide__arrow--next {
    right: 1rem;
}

.community-bubble {
    flex-shrink: 0;
    background: #f2f2f2;
    border-radius: 50px;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.community-bubble:hover {
    background: rgba(242, 242, 242, 0.8);
}

.community-bubble:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
}

.bubble-logo {
    height: 55px;
    width: auto;
    object-fit: contain;
    filter: brightness(0);
    transition: filter 0.3s ease;
}

.community-bubble.active .bubble-logo {
    filter: brightness(0) invert(1);
}

.bubble-name {
    font-size: 1rem;
    font-weight: 600;
    color: #000;
    height: 55px;
    transition: color 0.3s ease;
}

.community-bubble.active .bubble-name {
    color: white;
}

.close-btn {
    color: #000;
    font-size: 1.5rem;
    line-height: 1;
    transition: color 0.2s ease;
    flex-shrink: 0;
    align-self: center;
    margin-right: -40px;
}

.close-btn:hover {
    color: rgba(0, 0, 0, 0.6);
    text-decoration: none;
}

/* Detail View Styles */
.detail-view {
    animation: fadeIn 0.3s ease;
}
.community-header-main{
    max-height: 400px;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.community-detail-logo {
    max-width: 250px;
    min-width: 250px;
    filter: brightness(0) invert(1);
}

.community-header-image {
    max-width: 100%;
    height: auto;
    max-height: 200px;
}

.community-header-bg-image {
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background-size: cover;
    background-position: center;
}

.community-header-band {
    color: #14345a;
    /* border-bottom-left-radius: 30px;
    border-bottom-right-radius: 30px; */
    position: relative;
    z-index: 2;
}

.band-headline {
    font-size: 1.1rem;
    line-height: 1.2;
}

.band-subline {
    font-size: 0.95rem;
}

.rounded-corners-20 {
    border-radius: 20px;
}

.newsletter-btn {
    color: #fff;
}

.newsletter-btn:hover,
.newsletter-btn:focus,
.newsletter-btn:active {
    color: #fff;
    filter: brightness(0.92);
}

/* Responsive */
@media (max-width: 991.98px) {
    .splide-communities .splide__arrow--prev {
        left: 10px !important;
    }

    .splide-communities .splide__arrow--next {
        right: 10px !important;
    }
    .community-header-main{
        max-height: none;
    }
}

@media (max-width: 768px) {
    .bubble-content {
        padding: 0.5rem 1.25rem;
    }
    
    .bubble-logo {
        height: 45px;
    }

    .bubble-name {
        font-size: 0.85rem;
        height: 45px;
    }

    .community-detail-logo {
        max-width: 180px;
        min-width: 180px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .detail-view,
    .community-bubble,
    .bubble-logo {
        transition: none;
        animation: none;
    }
}
</style>
